(() => {
  const CATEGORY_CONFIG = {
    jackets: {
      label: 'JACKETS',
      title: 'Jackets, hoodies, and sweatshirts',
      description: 'Measure chest, waist, hips, arm length, and body height while standing naturally.',
      caption: 'Jackets, hoodies, and sweatshirts',
      imageKey: 'yxImageJackets',
      imageAlt: 'Jackets, hoodies, and sweatshirts measurement guide',
      guideIds: ['SG-01', 'SG-02', 'SG-03', 'SG-04', 'SG-05', 'SG-06', 'SG-07', 'SG-37', 'SG-39', 'SG-40'],
    },
    vests: {
      label: 'VESTS',
      title: 'Work and high-visibility vests',
      description: 'Measure chest, waist, hips, and body height. Keep the tape level and close to the body.',
      caption: 'Work and high-visibility vests',
      imageKey: 'yxImageVests',
      imageAlt: 'Work vests measurement guide',
      guideIds: ['SG-08', 'SG-09'],
    },
    tops: {
      label: 'TOPS & POLOS',
      title: 'T-shirts, polos, and scrub tops',
      description: 'Measure chest, waist, back length, and body height. Use the product-specific chart for the intended fit.',
      caption: 'T-shirts, polos, long-sleeve polos, and scrub tops',
      imageKey: 'yxImageTops',
      imageAlt: 'T-shirts, polos, and medical tops measurement guide',
      guideIds: ['SG-22', 'SG-27', 'SG-28', 'SG-29'],
    },
    pants: {
      label: 'PANTS & JOGGERS',
      title: 'Pants, shorts, and joggers',
      description: 'Measure waist, hips, thigh, inseam, and body height. Follow the inner leg when measuring inseam.',
      caption: 'Work pants, shorts, and joggers',
      imageKey: 'yxImagePants',
      imageAlt: 'Pants, shorts, and joggers measurement guide',
      guideIds: ['SG-10', 'SG-11', 'SG-12', 'SG-13', 'SG-14', 'SG-15', 'SG-16', 'SG-17', 'SG-24', 'SG-25', 'SG-26', 'SG-30', 'SG-38', 'SG-41'],
    },
    'bib-overalls': {
      label: 'BIB OVERALLS',
      title: 'Bib overalls',
      description: 'Measure waist, hips, thigh, inseam, and body height. Adjust the shoulder straps after selecting the correct lower-body fit.',
      caption: 'Bib overalls',
      imageKey: 'yxImageBibOveralls',
      imageAlt: 'Bib overalls measurement guide',
      guideIds: ['SG-18', 'SG-19', 'SG-20', 'SG-21'],
    },
    'rain-suit': {
      label: 'RAIN SUIT',
      title: 'Albatross Rain Suit',
      description: 'Use chest width, jacket length, sleeve length, waist, and inseam to choose the complete two-piece rain suit.',
      caption: 'Rain suit jacket and pants',
      imageKey: 'yxImageRainSuit',
      imageAlt: 'Rain suit jacket and pants measurement guide',
      guideIds: ['SG-23'],
    },
    footwear: {
      label: 'FOOTWEAR',
      title: 'Footwear size conversion',
      description: 'Choose the footwear model to convert its YURINOX US brand size to the corresponding UK and EU size.',
      caption: 'US, UK, and EU footwear size conversion',
      imageKey: null,
      imageAlt: '',
      guideIds: ['SG-31', 'SG-32', 'SG-33', 'SG-34', 'SG-35', 'SG-36'],
    },
  };

  const normalizeHandle = (product) => product?.liveHandle || product?.handle || '';

  const uniqueProducts = (products) => {
    const seen = new Set();
    return products.filter((product) => {
      const key = `${normalizeHandle(product)}::${product.gridId}`;
      if (!normalizeHandle(product) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const createCell = (tag, text) => {
    const cell = document.createElement(tag);
    cell.textContent = text || '—';
    return cell;
  };

  const sizeKey = (size) => String(size || '').trim()
    .replace(/^М$/, 'M')
    .replace(/\bAlto\b/i, 'Tall')
    .replace(/\bExtra Tall\b/i, 'Tall')
    .replace(/^XXL\b/i, '2XL')
    .replace(/\s*[×x]\s*/g, ' × ');

  // Verified against the nine EAPU03 chart rows and their flat waist ranges.
  const eapu03Sizes = {
    '30W × 29L': 'XS',
    '32W × 30L': 'S',
    '34W × 30L': 'M',
    '36W × 30L': 'L',
    '38W × 32L': 'XL',
    '40W × 32L': 'XXL',
    '42W × 32L': '3XL',
    '44W × 32L': '4XL',
    '46W × 32L': '5XL',
  };

  const shiftedSpectrHandles = new Set([
    'yurinox-spectr-premium-mens-heavy-duty-enhanced-visibility-vest',
    'unionworkwear-spectr-1-mens-enhanced-visibility-work-vest',
    'unionworkwear-spectr-2-mens-adjustable-enhanced-visibility-vest',
  ]);
  const shiftedSpectrSizes = { M: 'L', L: 'XL', XL: '2XL', '2XL': '3XL', '3XL': '4XL' };

  const alignInsulatedSpectr = (guide, sizes, productHandle) => {
    if (productHandle !== 'yurinox-spectr-mens-insulated-enhanced-visibility-work-vest' || guide.gridId !== 'SG-08' || guide.rows.length !== 5 || sizes.length !== 6) return null;
    const current = new Map(sizes.map((size) => [sizeKey(size), size]));
    if (current.size !== 6 || ['M', 'L', 'XL', '2XL', '3XL', '4XL'].some((size) => !current.has(size))) return null;
    const rows = guide.rows.map((row) => ({ ...row, size: current.get(sizeKey(row.size)) }));
    if (rows.some((row) => !row.size)) return null;
    // Estimated from the median of the last three SG-08 size steps; keep
    // the shared source guide unchanged because other vests use it too.
    rows.push({ ...rows[4], size: current.get('4XL'), chest: '51–51.9', waist: '45.6–47.2', hips: '49.2–50.4' });
    return { ...guide, rows };
  };

  const alignShiftedSpectr = (guide, sizes, productHandle) => {
    if (!shiftedSpectrHandles.has(productHandle) || guide.gridId !== 'SG-08' || guide.rows.length !== 5 || sizes.length !== 5) return null;
    const current = new Map(sizes.map((size) => [sizeKey(size), size]));
    if (current.size !== 5 || guide.rows.some((row) => !current.has(shiftedSpectrSizes[sizeKey(row.size)]))) return null;
    return { ...guide, rows: guide.rows.map((row) => ({ ...row, size: current.get(shiftedSpectrSizes[sizeKey(row.size)]) })) };
  };

  const alignStatusBibDraft = (guide, sizes, productHandle) => {
    if (productHandle !== 'unionworkwear-status-mens-shrink-resistant-work-bib-overalls' || guide.gridId !== 'SG-20' || guide.rows.length !== 11 || sizes.length !== 12) return null;
    const source = ['31W × 31L', '34W × 30L', '34W × 33L', '37W × 30L', '37W × 33L', '41W × 30L', '41W × 32L', '44W × 29L', '44W × 32L', '47W × 29L', '47W × 31L'];
    const target = ['30W × 30L', '34W × 30L', '34W × 34L', '36W × 30L', '36W × 34L', '40W × 30L', '40W × 34L', '42W × 30L', '42W × 32L', '46W × 30L', '46W × 32L'];
    if (guide.rows.some((row, index) => sizeKey(row.size) !== source[index])) return null;
    const current = new Map(sizes.map((size) => [sizeKey(size), size]));
    if (current.size !== 12 || [...target, '32W × 30L'].some((size) => !current.has(size))) return null;
    const rows = guide.rows.map((row, index) => ({ ...row, size: current.get(target[index]) }));
    // Owner approved this provisional interpolation for release on 2026-09-23;
    // the source image has no 32W × 30L row, so manufacturer verification remains open.
    rows.splice(1, 0, { ...guide.rows[0], size: current.get('32W × 30L'), waist: '31.5–33', hips: '38.4–39.8', thigh: '20.9–21.7', inseam: '31.8', estimated: true });
    return { ...guide, rows };
  };

  const alignGenesisBib = (guide, sizes, productHandle) => {
    if (productHandle !== 'yurinox-genesis-mens-stain-resistant-utility-work-bib-overalls' || guide.gridId !== 'SG-19' || guide.rows.length !== 11 || sizes.length !== 10) return null;
    const source = ['31W × 32L', '34W × 31L', '34W × 34L', '37W × 31L', '37W × 34L', '41W × 31L', '41W × 33L', '44W × 30L', '44W × 31L', '47W × 30L', '47W × 33L'];
    const target = ['30W × 30L', '32W × 30L', '32W × 32L', '34W × 30L', '34W × 32L', '38W × 30L', '38W × 32L', null, '40W × 32L', '44W × 30L', '44W × 32L'];
    if (guide.rows.some((row, index) => sizeKey(row.size) !== source[index])) return null;
    const current = new Map(sizes.map((size) => [sizeKey(size), size]));
    if (current.size !== 10 || target.some((size) => size && !current.has(size))) return null;
    // Keep the unmatched 44W × 30L row in the source; omit it only for Genesis.
    return { ...guide, rows: guide.rows.flatMap((row, index) => target[index] ? [{ ...row, size: current.get(target[index]) }] : []) };
  };

  const alignWorkmaxPants = (guide, sizes, productHandle) => {
    if (productHandle !== 'workmax-mens-water-resistant-softshell-double-knee-work-pants' || guide.gridId !== 'SG-15' || guide.rows.length !== 10 || sizes.length !== 5) return null;
    const source = ['31W × 29L', '31W × 32L', '34W × 30L', '34W × 32L', '37W × 30L', '37W × 33L', '41W × 31L', '41W × 33L', '44W × 31L', '44W × 34L'];
    const target = ['30W × 32L', '32W × 32L', '34W × 32L', '38W × 32L', '42W × 32L'];
    if (guide.rows.some((row, index) => sizeKey(row.size) !== source[index])) return null;
    const current = new Map(sizes.map((size) => [sizeKey(size), size]));
    if (current.size !== 5 || target.some((size) => !current.has(size))) return null;
    return { ...guide, rows: [1, 3, 5, 7, 9].map((index, targetIndex) => ({ ...guide.rows[index], size: current.get(target[targetIndex]) })) };
  };

  const numericGuideIds = new Set(['SG-10', 'SG-11', 'SG-12', 'SG-13', 'SG-14', 'SG-16', 'SG-17', 'SG-18', 'SG-21', 'SG-25', 'SG-41']);
  const firstNumber = (value) => Number(String(value || '').match(/\d+(?:\.\d+)?/)?.[0]);

  const alignNumericGuide = (guide, sizes) => {
    if (!numericGuideIds.has(guide.gridId) || guide.rows.length !== sizes.length) return null;
    const shorts = guide.gridId === 'SG-17';
    const sourceGroups = new Map();
    const variantGroups = new Map();

    guide.rows.forEach((row) => {
      const waist = firstNumber(row.size);
      if (!sourceGroups.has(waist)) sourceGroups.set(waist, []);
      sourceGroups.get(waist).push(row);
    });
    sizes.forEach((size) => {
      const match = sizeKey(size).match(shorts ? /^(\d+)W(?: (Tall))?$/ : /^(\d+)W × (\d+)L$/);
      if (!match) return;
      const waist = Number(match[1]);
      if (!variantGroups.has(waist)) variantGroups.set(waist, []);
      variantGroups.get(waist).push({ size, length: shorts ? Number(Boolean(match[2])) : Number(match[2]) });
    });

    const source = [...sourceGroups.values()].sort((a, b) => firstNumber(a[0].waist) - firstNumber(b[0].waist));
    const variants = [...variantGroups.entries()].sort((a, b) => a[0] - b[0]).map(([, items]) => items);
    if (source.length !== variants.length || variants.flat().length !== sizes.length) return null;

    const assigned = new Map();
    for (let index = 0; index < source.length; index += 1) {
      const rows = [...source[index]].sort((a, b) => firstNumber(a.inseam) - firstNumber(b.inseam));
      const options = [...variants[index]].sort((a, b) => a.length - b.length);
      if (rows.length !== options.length || !Number.isFinite(firstNumber(rows[0].waist))) return null;
      if (rows.some((row, position) => !Number.isFinite(firstNumber(row.inseam)) || (position > 0 && firstNumber(row.inseam) <= firstNumber(rows[position - 1].inseam)))) return null;
      if (options.some((option, position) => position > 0 && option.length <= options[position - 1].length)) return null;
      rows.forEach((row, position) => assigned.set(row, options[position].size));
    }
    return { ...guide, rows: guide.rows.map((row) => ({ ...row, size: assigned.get(row) })) };
  };

  const alignGuideToVariants = (guide, sizes, productHandle) => {
    if (!sizes.length || !Array.isArray(guide?.rows)) return guide;
    const shiftedSpectrGuide = alignShiftedSpectr(guide, sizes, productHandle);
    if (shiftedSpectrGuide) return shiftedSpectrGuide;
    const insulatedSpectrGuide = alignInsulatedSpectr(guide, sizes, productHandle);
    if (insulatedSpectrGuide) return insulatedSpectrGuide;
    const statusBibDraft = alignStatusBibDraft(guide, sizes, productHandle);
    if (statusBibDraft) return statusBibDraft;
    const genesisBib = alignGenesisBib(guide, sizes, productHandle);
    if (genesisBib) return genesisBib;
    const workmaxPants = alignWorkmaxPants(guide, sizes, productHandle);
    if (workmaxPants) return workmaxPants;
    if (guide.gridId === 'SG-38' && (sizes.length !== Object.keys(eapu03Sizes).length || sizes.some((size) => !eapu03Sizes[sizeKey(size)]))) return guide;
    const rows = sizes.map((size) => {
      const chartSize = guide.gridId === 'SG-38' ? eapu03Sizes[sizeKey(size)] : size;
      const row = guide.rows.find((candidate) => sizeKey(candidate.size) === sizeKey(chartSize));
      return row && { ...row, size };
    });
    if (rows.every(Boolean)) return { ...guide, rows };
    // Match numeric waist groups and inseam measurements, not row order.
    return alignNumericGuide(guide, sizes) || guide;
  };

  const init = (root) => {
    const isSpanish = root.dataset.yxLocale === 'es';
    const localizedTitles = JSON.parse(root.querySelector('[data-yx-size-guide-product-titles]')?.textContent || '{}');
    const productTitle = (product) => localizedTitles[normalizeHandle(product)] || product.title;
    const translations = {
      jackets: ['CHAQUETAS', 'Chaquetas y sudaderas con y sin capucha', 'Mide el pecho, la cintura, las caderas, el largo del brazo y la altura corporal de pie.', 'Chaquetas y sudaderas con y sin capucha', 'Guía de medidas para chaquetas y sudaderas con y sin capucha'],
      vests: ['CHALECOS', 'Chalecos de trabajo y alta visibilidad', 'Mide el pecho, la cintura, las caderas y la altura. Mantén la cinta nivelada y cerca del cuerpo.', 'Chalecos de trabajo y alta visibilidad', 'Guía de medidas para chalecos'],
      tops: ['TOPS Y POLOS', 'Camisetas, polos y tops médicos', 'Mide el pecho, la cintura, el largo de espalda y la altura. Usa la tabla específica del producto.', 'Camisetas, polos y tops médicos', 'Guía de medidas para camisetas, polos y tops médicos'],
      pants: ['PANTALONES Y JOGGERS', 'Pantalones, shorts y joggers', 'Mide la cintura, las caderas, el muslo, la entrepierna y la altura. Sigue el interior de la pierna al medir la entrepierna.', 'Pantalones de trabajo, shorts y joggers', 'Guía de medidas para pantalones, shorts y joggers'],
      'bib-overalls': ['PETOS', 'Petos de trabajo', 'Mide la cintura, las caderas, el muslo, la entrepierna y la altura. Ajusta los tirantes después de elegir el ajuste inferior correcto.', 'Petos de trabajo', 'Guía de medidas para petos'],
      'rain-suit': ['TRAJE DE LLUVIA', 'Traje de lluvia Albatross', 'Usa el ancho de pecho, el largo de chaqueta, el largo de manga, la cintura y la entrepierna.', 'Chaqueta y pantalón del traje de lluvia', 'Guía de medidas del traje de lluvia'],
      footwear: ['CALZADO', 'Conversión de tallas de calzado', 'Elige el modelo para convertir la talla YURINOX de EE. UU. a sus equivalentes del Reino Unido y la UE.', 'Conversión de tallas de calzado de EE. UU., Reino Unido y UE', ''],
    };
    const columnTranslations = {
      size: 'Talla', 'brand size': 'Talla', chest: 'Pecho / Chest', bust: 'Pecho', waist: 'Cintura / Waist', hips: 'Caderas / Hips', hip: 'Cadera / Hip',
      thigh: 'Muslo', inseam: 'Entrepierna', 'arm length': 'Largo de brazo', 'sleeve length': 'Largo de manga',
      height: 'Altura', 'back length': 'Largo de espalda', length: 'Largo', 'chest width': 'Ancho de pecho',
      'us size': 'Talla EE. UU.', 'uk size': 'Talla Reino Unido', 'eu size': 'Talla UE',
    };
    const tabs = [...root.querySelectorAll('[data-yx-size-guide-category]')];
    const panel = root.querySelector('[data-yx-size-guide-panel]');
    const visual = root.querySelector('[data-yx-size-guide-visual]');
    const image = root.querySelector('[data-yx-size-guide-category-image]');
    const footwearVisual = root.querySelector('[data-yx-size-guide-footwear-visual]');
    const caption = root.querySelector('[data-yx-size-guide-image-caption]');
    const categoryLabel = root.querySelector('[data-yx-size-guide-category-label]');
    const categoryTitle = root.querySelector('[data-yx-size-guide-category-title]');
    const categoryDescription = root.querySelector('[data-yx-size-guide-category-description]');
    const productSelect = root.querySelector('[data-yx-size-guide-product-select]');
    const selection = root.querySelector('[data-yx-size-guide-selection]');
    const chartName = root.querySelector('[data-yx-size-guide-chart-name]');
    const productLink = root.querySelector('[data-yx-size-guide-product-link]');
    const tableScroll = root.querySelector('[data-yx-size-guide-table-scroll]');
    const tableHead = root.querySelector('[data-yx-size-guide-table-head]');
    const tableBody = root.querySelector('[data-yx-size-guide-table-body]');
    const loading = root.querySelector('[data-yx-size-guide-loading]');
    const error = root.querySelector('[data-yx-size-guide-error]');
    const units = root.querySelector('[data-yx-size-guide-units]');
    const supportChatLink = root.querySelector('[data-yx-size-guide-chat]');
    const catalogUrl = root.dataset.yxCatalogUrl;

    if (!tabs.length || !panel || !productSelect || !catalogUrl) return;

    const findChatActivator = () => {
      const chat = document.querySelector('shopify-chat');
      const chatRoot = chat?.shadowRoot;
      const runtime = chatRoot?.querySelector('shopify-agent-runtime');
      const launcherRoot = runtime?.shadowRoot || chatRoot;
      return launcherRoot?.querySelector('[part~="activator"]') || null;
    };

    if (supportChatLink) {
      let chatOpenPending = false;

      supportChatLink.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        event.preventDefault();
        if (chatOpenPending) return;
        chatOpenPending = true;

        const fallbackUrl = supportChatLink.href;
        const deadline = window.performance.now() + 3000;
        const openChat = () => {
          const activator = findChatActivator();
          if (activator) {
            chatOpenPending = false;
            if (activator.getAttribute('aria-expanded') !== 'true') activator.click();
            return;
          }

          if (window.performance.now() < deadline) {
            window.setTimeout(openChat, 50);
            return;
          }

          chatOpenPending = false;
          window.location.assign(fallbackUrl);
        };

        openChat();
      });
    }

    let catalog = null;
    let activeCategory = 'jackets';
    let activeProducts = [];
    let productRequest = 0;

    const setBusy = (busy) => {
      productSelect.disabled = busy;
      loading.hidden = !busy;
      if (busy) {
        tableScroll.hidden = true;
        selection.hidden = true;
      }
    };

    const renderTable = (guide) => {
      const columns = Array.isArray(guide?.columns) ? guide.columns : [];
      const rows = Array.isArray(guide?.rows) ? guide.rows : [];
      if (!columns.length || !rows.length) return false;

      const headRow = document.createElement('tr');
      columns.forEach((column) => {
        const cell = createCell('th', isSpanish ? (columnTranslations[String(column.label || '').toLowerCase()] || column.label) : column.label);
        cell.scope = 'col';
        headRow.append(cell);
      });

      const bodyFragment = document.createDocumentFragment();
      rows.forEach((row) => {
        const tableRow = document.createElement('tr');
        columns.forEach((column, index) => {
          const rowValue = index === 0 && isSpanish
            ? String(row[column.key] || '').replace(/Extra Tall/gi, 'Extra alto').replace(/Tall/gi, 'Alto')
            : row[column.key];
          const cell = createCell(index === 0 ? 'th' : 'td', rowValue);
          if (index === 0) cell.scope = 'row';
          tableRow.append(cell);
        });
        bodyFragment.append(tableRow);
      });

      tableHead.replaceChildren(headRow);
      tableBody.replaceChildren(bodyFragment);
      tableScroll.scrollLeft = 0;
      tableScroll.hidden = false;
      return true;
    };

    const renderProduct = (productIndex) => {
      const request = ++productRequest;
      const product = activeProducts[Number(productIndex)] || activeProducts[0];
      const guide = product && catalog.guides.find((item) => item.gridId === product.gridId);
      if (!product || !guide || !renderTable(guide)) {
        selection.hidden = true;
        tableScroll.hidden = true;
        error.hidden = false;
        return;
      }

      error.hidden = true;
      selection.hidden = false;
      const chartDetail = String(guide.name || '').split(' — ').slice(1).join(' — ')
        .replace(/US Regular/g, 'Tallaje regular de EE. UU.')
        .replace(/\bUS\b/g, 'EE. UU.').replace(/\bNavy\b/g, 'Azul marino');
      chartName.textContent = (isSpanish
        ? [productTitle(product), chartDetail].filter(Boolean).join(' — ')
        : guide.name || product.title) || (isSpanish ? 'Tabla de tallas del producto' : 'Product size chart');
      productLink.href = `${isSpanish ? '/es' : ''}/products/${encodeURIComponent(normalizeHandle(product))}`;
      productLink.hidden = !normalizeHandle(product);
      units.textContent = activeCategory === 'footwear'
        ? (isSpanish ? 'La talla de marca se muestra en tallaje de EE. UU. con equivalencias del Reino Unido y la UE.' : 'Brand size is shown in US sizing with UK and EU equivalents.')
        : (isSpanish ? 'Las medidas de las prendas se muestran en pulgadas.' : 'Garment measurements are shown in inches.');

      fetch(`${isSpanish ? '/es' : ''}/products/${encodeURIComponent(normalizeHandle(product))}.js`, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Product size request failed with ${response.status}.`);
          return response.json();
        })
        .then((currentProduct) => {
          if (request !== productRequest) return;
          const options = Array.isArray(currentProduct.options) ? currentProduct.options : [];
          const sizeIndex = options.findIndex((option) => /size|talla/i.test(typeof option === 'string' ? option : option?.name));
          if (sizeIndex < 0) return;
          const sizes = [...new Set((currentProduct.variants || []).map((variant) => variant[`option${sizeIndex + 1}`]).filter(Boolean))];
          renderTable(alignGuideToVariants(guide, sizes, normalizeHandle(product)));
        })
        .catch((error) => console.warn('YURINOX current product sizes could not be loaded.', error));
    };

    const populateProducts = (categoryKey) => {
      const config = CATEGORY_CONFIG[categoryKey];
      activeProducts = uniqueProducts(catalog.products.filter((product) => config.guideIds.includes(product.gridId)))
        .sort((a, b) => String(a.title || normalizeHandle(a)).localeCompare(String(b.title || normalizeHandle(b))));

      const options = document.createDocumentFragment();
      activeProducts.forEach((product, index) => {
        const option = document.createElement('option');
        option.value = String(index);
        option.textContent = productTitle(product) || normalizeHandle(product).replace(/-/g, ' ');
        options.append(option);
      });
      productSelect.replaceChildren(options);
      productSelect.disabled = !activeProducts.length;
      renderProduct(0);
    };

    const renderCategory = (categoryKey, focusTab = false) => {
      const config = CATEGORY_CONFIG[categoryKey];
      if (!config || !catalog) return;
      activeCategory = categoryKey;

      tabs.forEach((tab) => {
        const selected = tab.dataset.yxSizeGuideCategory === categoryKey;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focusTab) tab.focus();
      });

      const localized = isSpanish ? translations[categoryKey] : null;
      categoryLabel.textContent = localized ? localized[0] : config.label;
      categoryTitle.textContent = localized ? localized[1] : config.title;
      categoryDescription.textContent = localized ? localized[2] : config.description;
      caption.textContent = localized ? localized[3] : config.caption;

      const isFootwear = categoryKey === 'footwear';
      image.hidden = isFootwear;
      footwearVisual.hidden = !isFootwear;
      visual.classList.toggle('yx-size-guide-page__visual--footwear', isFootwear);
      if (!isFootwear) {
        image.src = root.dataset[config.imageKey];
        image.alt = localized ? localized[4] : config.imageAlt;
        image.width = 1164;
        image.height = 1351;
      }

      populateProducts(categoryKey);
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => renderCategory(tab.dataset.yxSizeGuideCategory));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        renderCategory(tabs[nextIndex].dataset.yxSizeGuideCategory, true);
      });
    });

    productSelect.addEventListener('change', () => renderProduct(productSelect.value));

    setBusy(true);
    fetch(catalogUrl, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Size guide catalog request failed with ${response.status}.`);
        return response.json();
      })
      .then((data) => {
        catalog = data;
        loading.hidden = true;
        renderCategory(activeCategory);
      })
      .catch((requestError) => {
        console.warn('YURINOX Size Guide page could not load its catalog.', requestError);
        setBusy(false);
        loading.hidden = true;
        error.hidden = false;
      });
  };

  document.querySelectorAll('[data-yx-size-guide-page]').forEach(init);
})();
